/**
 * The top sky, drawn as hairline line art: clouds, birds, and by night the
 * stars and the moon. No fills or glows. Each shape has a silhouette stroke
 * and at most one dim crease, and solids (clouds, the moon) are filled with
 * the paper so whatever passes behind them stays hidden. One silhouette
 * at a time is bright: the sun by day, the moon by night, else one cloud.
 * Nothing answers the pointer; layers drift on their own at speeds set by
 * depth, and scroll by depth, so the parallax runs by itself.
 */
import { hex, mixRgb, type Rgb } from "./sky"

// ---- Ink -------------------------------------------------------------------

interface Ink {
  readonly paper: Rgb
  /** The one bright stroke. */
  readonly hi: Rgb
  /** Silhouettes. */
  readonly sil: Rgb
  /** Creases and far details. */
  readonly dim: Rgb
}

/** Mirrors the page tokens: bg, fg, and two steps toward the line colors. */
const DAY_INK: Ink = {
  paper: hex("#ffffff"),
  hi: hex("#1a1a1a"),
  sil: hex("#a6a6a6"),
  dim: hex("#dadada"),
}
const NIGHT_INK: Ink = {
  paper: hex("#0e1022"),
  hi: hex("#ededf3"),
  sil: hex("#5f6284"),
  dim: hex("#2c2f4a"),
}

/** Low-sun tones: rosy peach at dawn, amber at dusk. */
const DAWN_WARM = hex("#f2967e")
const DUSK_WARM = hex("#f08232")

const css = ([r, g, b]: Rgb, alpha = 1) =>
  `rgb(${Math.round(r)} ${Math.round(g)} ${Math.round(b)} / ${alpha})`

// ---- Random ------------------------------------------------------------------

/** Small seeded generator, so a layout is the same on every resize. */
function seeded(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296
  }
}

// ---- Model -------------------------------------------------------------------

interface Puff {
  readonly dx: number
  readonly dy: number
  readonly r: number
}

/** A thin wisp of cirrus: an open stroke, nothing to fill. */
interface Streak {
  readonly dx: number
  readonly dy: number
  readonly length: number
  /** Sag at the middle, px; negative arches up. */
  readonly bend: number
}

export type CloudKind = "cumulus" | "tower" | "bank" | "puff" | "cirrus"

export interface Cloud {
  readonly kind: CloudKind
  /** Start position, in px; it drifts from here. */
  readonly x0: number
  /** Baseline, in px from the top of the band. */
  readonly y: number
  readonly width: number
  /** Height of the flat base. */
  readonly base: number
  readonly puffs: readonly Puff[]
  /** Cirrus only; solid kinds have none. */
  readonly streaks: readonly Streak[]
  /** Parallax depth: 0 sits still, 1 moves the most. */
  readonly depth: number
  /** Drift speed, px/s. */
  readonly speed: number
  /** Tallest point above the baseline, for hit testing. */
  readonly top: number
}

interface Star {
  readonly x: number
  readonly y: number
  readonly sparkle: boolean
  readonly phase: number
}

interface Bird {
  readonly ox: number
  readonly oy: number
  readonly size: number
  readonly phase: number
}

export interface Flock {
  x: number
  readonly y: number
  /** +1 flies right, -1 flies left. */
  readonly dir: number
  readonly speed: number
  readonly birds: readonly Bird[]
}

export interface Sky {
  readonly width: number
  readonly height: number
  readonly clouds: readonly Cloud[]
  readonly stars: readonly Star[]
}

/** Far clouds lean thin and high, near ones heaped and solid. */
const LAYERS = [
  { depth: 0.35, scale: 0.7, speed: 6, per: 380, kinds: ["cirrus", "bank", "cirrus", "puff", "cumulus"] },
  { depth: 0.8, scale: 1, speed: 15, per: 400, kinds: ["cumulus", "tower", "puff", "bank", "cumulus"] },
] as const satisfies ReadonlyArray<{ kinds: readonly CloudKind[] } & Record<string, unknown>>

/**
 * One cloud's outline, at width `w`: puffs heaped on a flat base, or for
 * cirrus a few thin streaks. Every kind is a little lopsided.
 */
function shapeCloud(kind: CloudKind, w: number, rand: () => number) {
  const puffs: Puff[] = []
  const streaks: Streak[] = []
  // Where the crown sits, left or right of center.
  const lean = (rand() - 0.5) * 0.5
  let width = w
  let base = w * 0.16

  const heap = (n: number, spread: number, size: number, peak: number) => {
    for (let p = 0; p < n; p++) {
      const along = (p + 0.5) / n - 0.5
      const nearCrown = 1 - Math.min(1, Math.abs(along - lean * 0.5) * 2)
      const r = width * (size + peak * nearCrown + rand() * 0.05)
      puffs.push({ dx: along * width * spread, dy: -base * 0.5 - r * 0.45, r })
    }
  }

  switch (kind) {
    case "cumulus":
      heap(3 + Math.floor(rand() * 2), 0.62, 0.18, 0.14)
      break
    case "tower": {
      // A heap with a second, smaller heap stacked on its crown.
      width = w * 0.85
      heap(3, 0.6, 0.2, 0.08)
      const crown = puffs.reduce((a, b) => (b.r > a.r ? b : a))
      const r = width * (0.2 + rand() * 0.05)
      puffs.push({ dx: crown.dx + (rand() - 0.5) * r * 0.6, dy: crown.dy - crown.r * 0.75, r })
      puffs.push({ dx: crown.dx + r * 0.7 * Math.sign(lean || 1), dy: crown.dy - crown.r * 0.35, r: r * 0.7 })
      break
    }
    case "bank":
      // Long and low: many small puffs on a thin base.
      width = w * (1.6 + rand() * 0.5)
      base = w * 0.1
      heap(6 + Math.floor(rand() * 3), 0.82, 0.06, 0.06)
      break
    case "puff":
      width = w * 0.55
      heap(2, 0.5, 0.26, 0.08)
      break
    case "cirrus": {
      width = w * (1.4 + rand() * 0.6)
      base = 0
      const n = 2 + Math.floor(rand() * 2)
      for (let i = 0; i < n; i++) {
        const length = width * (0.5 + rand() * 0.5)
        streaks.push({
          dx: (rand() - 0.5) * (width - length),
          dy: -i * (5 + rand() * 4),
          length,
          bend: (rand() - 0.3) * 6,
        })
      }
      break
    }
  }
  const top = Math.max(base, ...puffs.map((p) => -p.dy + p.r), ...streaks.map((k) => -k.dy + 6))
  return { width, base, puffs, streaks, top }
}

/** Shapes shrink on narrow screens, down to half size on a phone. */
const sizeScale = (width: number) => Math.max(0.5, Math.min(1, width / 1100))
/** And sit higher, so they stay above the name and bio. */
const rowScale = (width: number) => sizeScale(width) * 1.16 - 0.16

/** Lay out clouds and stars for a band of this size. */
export function buildSky(width: number, height: number): Sky {
  const rand = seeded(7)
  // Phones get smaller clouds, fewer of them, sitting higher, so they stay
  // clear of the name and bio.
  const fit = sizeScale(width)
  const clouds: Cloud[] = []
  for (const layer of LAYERS) {
    const count = Math.max(1, Math.round(width / layer.per))
    for (let i = 0; i < count; i++) {
      const kind = layer.kinds[i % layer.kinds.length]!
      const shape = shapeCloud(kind, (90 + rand() * 70) * layer.scale * fit, rand)
      // Cirrus rides highest; towers sit low enough to show their height.
      const row = kind === "cirrus" ? 0.18 : layer.depth < 0.5 ? 0.3 : 0.45
      clouds.push({
        kind,
        ...shape,
        x0: ((i + 0.2 + rand() * 0.6) / count) * width,
        y: height * (row + rand() * 0.15) * rowScale(width),
        depth: layer.depth,
        speed: layer.speed * (0.8 + rand() * 0.4) * (kind === "cirrus" ? 1.4 : 1),
      })
    }
  }

  const stars: Star[] = []
  const starCount = Math.round((width * height) / 7000)
  for (let i = 0; i < starCount; i++) {
    stars.push({
      x: rand() * width,
      y: rand() * height * 0.75,
      sparkle: rand() < 0.14,
      phase: rand() * Math.PI * 2,
    })
  }
  return { width, height, clouds, stars }
}

/** A loose V of three to five birds, entering from one side. */
export function spawnFlock(width: number, height: number, rand: () => number = Math.random): Flock {
  const dir = rand() < 0.5 ? 1 : -1
  const n = 3 + Math.floor(rand() * 3)
  const birds: Bird[] = []
  for (let i = 0; i < n; i++) {
    const rank = Math.ceil(i / 2) * (i % 2 === 0 ? 1 : -1)
    birds.push({
      ox: -dir * Math.abs(rank) * (14 + rand() * 6),
      oy: rank * (9 + rand() * 4),
      size: 6.5 + rand() * 2.5,
      phase: rand() * Math.PI * 2,
    })
  }
  return {
    x: dir > 0 ? -60 : width + 60,
    y: height * (0.18 + rand() * 0.25) * rowScale(width),
    dir,
    speed: 26 + rand() * 14,
    birds,
  }
}

/** The cloud's on-screen x, with drift and wrap. */
export function cloudX(cloud: Cloud, sky: Sky, clock: number): number {
  const span = sky.width + cloud.width * 2
  const x = (cloud.x0 + cloud.speed * clock + cloud.width) % span
  return x - cloud.width
}

// ---- Frame -------------------------------------------------------------------

export interface FrameInput {
  readonly sky: Sky
  readonly flock: Flock | null
  /** Animation clock, s. */
  readonly clock: number
  /** 0 in the light theme, 1 in the dark; eased between. */
  readonly dark: number
  /** 0 by day, 1 in deep night. */
  readonly night: number
  /** Width of the page's text column, px; clouds fade behind it. */
  readonly column: number
  /** Page scroll, px. */
  readonly scroll: number
  /** Sun on screen (px), or null while it is down. */
  readonly sun: { x: number; y: number } | null
  /**
   * The golden hours: 0 most of the day, 1 with the sun on the horizon.
   * `glowAt` is where the light comes from, even once the sun has set.
   */
  readonly warmth: number
  readonly dawn: boolean
  readonly glowAt: { x: number; y: number }
  /**
   * Moon on screen (px) with its phase, or null while it is down. `litSide`
   * is which way the sun lies on screen (1 right, -1 left).
   */
  readonly moon: { x: number; y: number; illumination: number; litSide: 1 | -1 } | null
}

const SCROLL_LAG = 0.35
const MOON_RADIUS = 11

/** 0 at `a`, 1 at `b`, eased; `a` may be greater than `b`. */
const smoothstep = (a: number, b: number, v: number) => {
  const t = Math.max(0, Math.min(1, (v - a) / (b - a)))
  return t * t * (3 - 2 * t)
}
const SUN_RADIUS = 9

/**
 * Where a body sits in the band, from its place in the sky. Looking south:
 * it rises at the left edge, climbs as it crosses, and sets at the right,
 * so the same time of day always lands in the same place.
 */
export function placeInSky(elevation: number, azimuth: number, width: number, height: number) {
  const rad = Math.PI / 180
  const high = Math.sin(Math.max(-5, Math.min(90, elevation)) * rad)
  return {
    x: width * (0.5 - 0.42 * Math.sin(azimuth * rad)),
    y: height * (0.6 - 0.5 * high) * rowScale(width),
  }
}

const shift = (depth: number, input: Pick<FrameInput, "scroll">) => ({
  x: 0,
  y: input.scroll * SCROLL_LAG * (1 - depth),
})

export function drawSky(ctx: CanvasRenderingContext2D, input: FrameInput): void {
  const { sky, clock, dark, night } = input
  const ink: Ink = {
    paper: mixRgb(DAY_INK.paper, NIGHT_INK.paper, dark),
    hi: mixRgb(DAY_INK.hi, NIGHT_INK.hi, dark),
    sil: mixRgb(DAY_INK.sil, NIGHT_INK.sil, dark),
    dim: mixRgb(DAY_INK.dim, NIGHT_INK.dim, dark),
  }
  ctx.clearRect(0, 0, sky.width, sky.height)

  // Dawn and dusk: the one place the sky takes a colour. Peach at dawn,
  // amber at dusk, glowing from where the sun is (or just went), with a
  // faint wash across the whole band. Lines pick up the same tone below.
  const { warmth } = input
  const warm = input.dawn ? DAWN_WARM : DUSK_WARM
  if (warmth > 0.01) {
    const strength = warmth * (0.32 + 0.12 * dark)
    // On the dark page a broad orange wash turns muddy; keep it to the glow.
    ctx.fillStyle = css(warm, strength * (0.35 - 0.25 * dark))
    ctx.fillRect(0, 0, sky.width, sky.height)
    const { x, y } = input.glowAt
    const radius = Math.max(sky.width * 0.5, 320)
    const glow = ctx.createRadialGradient(x, y, 0, x, y, radius)
    glow.addColorStop(0, css(warm, strength))
    glow.addColorStop(0.45, css(warm, strength * 0.35))
    glow.addColorStop(1, css(warm, 0))
    ctx.fillStyle = glow
    ctx.fillRect(0, 0, sky.width, sky.height)
  }
  // Clouds catch the low light: warm outlines, a blush on their paper.
  const cloudInk = mixRgb(ink.sil, warm, warmth * 0.75)
  const cloudPaper = mixRgb(ink.paper, warm, warmth * 0.12)
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  // The bright stroke: the sun by day or the moon
  // by night, else the biggest near cloud, so the resting frame always has
  // a place to start.
  const bright: Cloud | "sun" | "moon" | null =
    (input.sun && night < 0.5
      ? "sun"
      : input.moon && night >= 0.5
        ? "moon"
        : sky.clouds.reduce<Cloud | null>(
            (best, c) => (c.depth > 0.5 && c.puffs.length > 0 && (!best || c.width > best.width) ? c : best),
            null
          ))

  // Stars: dots, and a few four-point sparkles, fading in with the night.
  if (night > 0) {
    const s = shift(0.1, input)
    for (const star of sky.stars) {
      const twinkle = 0.55 + 0.45 * Math.sin(clock * 1.3 + star.phase)
      const x = star.x + s.x
      const y = star.y + s.y
      ctx.strokeStyle = css(ink.sil, night * twinkle)
      ctx.fillStyle = ctx.strokeStyle
      if (star.sparkle) {
        const r = 2.5 + twinkle
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(x - r, y)
        ctx.lineTo(x + r, y)
        ctx.moveTo(x, y - r)
        ctx.lineTo(x, y + r)
        ctx.stroke()
      } else {
        ctx.beginPath()
        ctx.arc(x, y, 0.9, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }

  // Sun: a ring with short rays, turning slowly.
  if (input.sun) {
    const s = shift(0.15, input)
    const x = input.sun.x + s.x
    const y = input.sun.y + s.y
    const r = SUN_RADIUS
    ctx.lineWidth = 1.25
    ctx.strokeStyle = css(mixRgb(bright === "sun" ? ink.hi : ink.sil, warm, warmth))
    ctx.fillStyle = css(mixRgb(ink.paper, warm, warmth * 0.15))
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.beginPath()
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + clock * 0.08
      ctx.moveTo(x + Math.cos(a) * (r + 4), y + Math.sin(a) * (r + 4))
      ctx.lineTo(x + Math.cos(a) * (r + (i % 2 ? 7 : 9)), y + Math.sin(a) * (r + (i % 2 ? 7 : 9)))
    }
    ctx.stroke()
  }

  // Moon: a dim full disc, the lit part drawn over it as a silhouette.
  if (input.moon) {
    const s = shift(0.15, input)
    const { illumination, litSide: side } = input.moon
    const x = input.moon.x + s.x
    const y = input.moon.y + s.y
    const r = MOON_RADIUS
    ctx.lineWidth = 1.25
    ctx.fillStyle = css(ink.paper)
    ctx.strokeStyle = css(ink.dim)
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    if (illumination > 0.03) {
      // Lit limb on the sun's side, closed by the terminator: a
      // half-ellipse that bulges out past half-full.
      const terminator = r * Math.abs(1 - 2 * illumination)
      const crescent = illumination < 0.5
      ctx.strokeStyle = css(bright === "moon" ? ink.hi : ink.sil, Math.max(0.35, 1 - (1 - night) * 0.6))
      ctx.beginPath()
      ctx.ellipse(x, y, r, r, 0, -Math.PI / 2, Math.PI / 2, side < 0)
      ctx.ellipse(x, y, terminator, r, 0, Math.PI / 2, -Math.PI / 2, crescent === side > 0)
      ctx.stroke()
    }
  }

  // Clouds, far layer first so near ones cover it. Passing behind the text
  // column their strokes fade back, so lines never cross the name; their
  // paper fill stays, so they still hide the sun and moon.
  const columnHalf = input.column / 2
  for (const cloud of [...sky.clouds].sort((a, b) => a.depth - b.depth)) {
    const s = shift(cloud.depth, input)
    const x = cloudX(cloud, sky, clock) + s.x
    const y = cloud.y + s.y
    const isBright = bright === cloud
    const behindText =
      sky.width >= input.column * 1.4
        ? smoothstep(columnHalf + cloud.width / 2 + 24, columnHalf - cloud.width * 0.2, Math.abs(x - sky.width / 2))
        : 0
    const alpha = 1 - 0.75 * behindText
    const outline = isBright
      ? mixRgb(ink.hi, warm, warmth * 0.5)
      : cloud.depth < 0.5
        ? mixRgb(cloudInk, ink.dim, 0.5 * (1 - warmth))
        : cloudInk
    const half = cloud.width / 2
    const lineWidth = isBright ? 1.5 : 1.25

    if (cloud.kind === "cirrus") {
      // Wisps are open strokes: nothing to fill, nothing to hide.
      ctx.lineWidth = lineWidth
      ctx.strokeStyle = css(isBright ? ink.hi : mixRgb(ink.sil, ink.dim, 0.4), alpha)
      ctx.beginPath()
      for (const k of cloud.streaks) {
        const x0 = x + k.dx - k.length / 2
        ctx.moveTo(x0, y + k.dy)
        ctx.quadraticCurveTo(x0 + k.length * 0.5, y + k.dy + k.bend, x0 + k.length, y + k.dy - k.bend * 0.3)
      }
      ctx.stroke()
      continue
    }

    const shape = () => {
      ctx.beginPath()
      ctx.roundRect(x - half, y - cloud.base, cloud.width, cloud.base, cloud.base / 2)
      for (const p of cloud.puffs) {
        ctx.moveTo(x + p.dx + p.r, y + p.dy)
        ctx.arc(x + p.dx, y + p.dy, p.r, 0, Math.PI * 2)
      }
    }
    // Union outline: stroke every part at double width, then fill them all
    // with paper, which leaves only the outside edge.
    shape()
    ctx.lineWidth = lineWidth * 2
    ctx.strokeStyle = css(outline, alpha)
    ctx.stroke()
    ctx.fillStyle = css(cloudPaper)
    ctx.fill()

    // One crease: the shaded underside of the crown.
    const crown = cloud.puffs.reduce((a, b) => (b.r > a.r ? b : a))
    ctx.lineWidth = 1
    ctx.strokeStyle = css(mixRgb(ink.dim, warm, warmth * 0.6), alpha)
    ctx.beginPath()
    ctx.arc(x + crown.dx, y + crown.dy, crown.r * 0.62, Math.PI * 0.62, Math.PI * 0.92)
    ctx.stroke()
  }

  // Birds: two wing strokes each, flapping out of step.
  if (input.flock) {
    const flock = input.flock
    const s = shift(0.55, input)
    ctx.lineWidth = 1.25
    ctx.strokeStyle = css(ink.sil)
    ctx.beginPath()
    for (const bird of flock.birds) {
      const bx = flock.x + bird.ox + s.x
      const by = flock.y + bird.oy + s.y + Math.sin(clock * 1.7 + bird.phase) * 2
      const k = bird.size
      const tip = k * (0.1 + 0.4 * Math.sin(clock * 9 + bird.phase))
      ctx.moveTo(bx - k, by - tip)
      ctx.quadraticCurveTo(bx - k * 0.45, by - k * 0.5, bx, by)
      ctx.quadraticCurveTo(bx + k * 0.45, by - k * 0.5, bx + k, by - tip)
    }
    ctx.stroke()
  }
}
